import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatHistory from './ChatHistory';
import Prompts from './Prompts';
import FineTuningDashboard from './FineTuningDashboard';
import QAValidation from './consultant/QAValidation';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import './ChatbotTraining.css';

type TabType = 'chat-history' | 'prompts' | 'training' | 'qa-validation';

const ChatbotTraining: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('chat-history');

  // Initialize tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs: TabType[] = ['chat-history', 'prompts', 'training', 'qa-validation'];
    // Redirect old feedback-qa hash to chat-history
    if (hash === 'feedback-qa') {
      navigate('/chatbot-training#chat-history', { replace: true });
      setActiveTab('chat-history');
      return;
    }
    const typedHash = hash as TabType;
    if (hash && validTabs.includes(typedHash)) {
      setActiveTab(typedHash);
    } else {
      // Default to first tab if no valid hash
      setActiveTab('chat-history');
      // Update URL if no hash or invalid hash
      if (!hash || !validTabs.includes(typedHash)) {
        navigate('/chatbot-training#chat-history', { replace: true });
      }
    }
  }, [location.hash, navigate]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/chatbot-training#${tab}`, { replace: true });
  };

  const tabs = [
    { id: 'chat-history' as TabType, label: 'Chat History', icon: '💬' },
    { id: 'prompts' as TabType, label: 'Prompts', icon: '🤖' },
    { id: 'training' as TabType, label: 'Training', icon: '🎯' },
    { id: 'qa-validation' as TabType, label: 'QA Validation', icon: <ClipboardDocumentCheckIcon strokeWidth={1.8} /> },
  ];

  return (
    <div className="chatbot-training-container">
      <div className="chatbot-training-header">
        <h1>Chatbot Training</h1>
        <p>Manage conversations, prompts, Q&A review, and model training</p>
      </div>

      <div className="chatbot-training-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="chatbot-training-content">
        {activeTab === 'chat-history' && <ChatHistory />}
        {activeTab === 'prompts' && <Prompts />}
        {activeTab === 'training' && <FineTuningDashboard />}
        {activeTab === 'qa-validation' && <QAValidation embedded={true} clientId={localStorage.getItem('client_id') || undefined} />}
      </div>
    </div>
  );
};

export default ChatbotTraining;

